import React from 'react';
import { Link, useLocation } from 'react-router-dom';

// Logo PG jako statyczny wektor (outline liter, wyeksportowany z Baskervville).
// Zastępuje żywy tekst renderowany fontem - eliminuje wszelkie różnice
// w rasteryzacji/FOUT/subpixel jitter między stronami, bo to teraz stały
// kształt (path), a nie tekst zależny od załadowania czcionki.
// viewBox oryginału: 0 0 66.005264 35.569923 (proporcje ok. 1.856 : 1)

const GlobalLogo: React.FC = () => {
  const location = useLocation();

  // Na stronie głównej logo nie jest pokazywane - tam jest osobny,
  // animowany monogram w Intro.tsx
  if (location.pathname === '/') {
    return null;
  }

  // Rozmiar SVG żyje w CSS (.gallery-page-logo svg w App.css), PRZYPIĘTY
  // do jednej wspólnej zmiennej --nav-font-size przez stały mnożnik.
  //
  // WAŻNE: klasa "gallery-page-logo--about" jest dodawana TUTAJ, w JS,
  // zamiast liczyć na selektor CSS ".about-page .gallery-page-logo" -
  // ten drugi NIGDY nie mógł zadziałać, bo GlobalLogo jest renderowany
  // RAZEM Z <Routes> w App.tsx (żeby uniknąć duplikatów logo przy każdej
  // stronie), więc w prawdziwym drzewie DOM nie jest potomkiem elementu
  // o klasie "about-page" zwracanego przez komponent About - to dwa
  // różne poddrzewa. Selektor potomka fizycznie nie mógł trafić,
  // niezależnie od tego jak poprawna była reszta CSS. Klasa dodana
  // bezpośrednio tutaj nie zależy od zagnieżdżenia w ogóle.
  const isAboutPage = location.pathname === '/about';
  const logoClassName = isAboutPage
    ? 'gallery-page-logo gallery-page-logo--about'
    : 'gallery-page-logo';

  return (
    <Link
      to="/"
      className={logoClassName}
      aria-label="Positive Grain - strona główna"
    >
      <svg
        viewBox="0 0 66.005264 35.569923"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          fill="currentColor"
          d="m 10.64121,18.033008 v 8.904883 q 0,2.530078 0.27285,3.770312 0.29766,1.21543 1.0666,2.00918 0.66973,0.694531 1.53789,1.041797 0.89297,0.32246 2.33164,0.396875 v 0.570507 h -15.85019 v -0.570507 q 1.43867,-0.07442 2.30683,-0.396875 0.89297,-0.347266 1.5627,-1.041797 0.79375,-0.79375 1.0666,-2.00918 0.27285,-1.21543 0.27285,-3.770312 V 8.656836 q 0,-2.133203 -0.19844,-3.274219 -0.17363,-1.16582 -0.62011,-1.885156 -0.64492,-0.992187 -1.63711,-1.463476 -0.99219,-0.496094 -2.57969,-0.595313 h -0.17363 v -0.570508 h 14.95722 q 3.91914,0 5.7795,0.396875 1.86035,0.396875 3.39824,1.5875 3.52226,2.678907 3.52226,7.14375 0,4.539258 -3.57187,6.647656 -1.86035,1.091407 -4.51445,1.314649 -0.84336,0.07441 -4.06797,0.07441 z m 0,-0.79375 h 2.43086 q 2.45566,0 3.77031,-0.347266 1.33945,-0.37207 2.38125,-1.339453 2.43086,-2.207617 2.43086,-5.878711 0,-2.033984 -0.71934,-3.720703 -0.69453,-1.711523 -2.00918,-2.778125 -1.51308,-1.265039 -3.59668,-1.488281 -0.5705,-0.04961 -2.60449,-0.04961 h -2.08359 z m 49.78301,-16.371094 0.29765,10.790039 -0.57051,0.04961 q -1.71152,-5.506641 -4.39043,-8.185547 -2.6789,-2.703711 -6.47402,-2.703711 -2.95176,0 -5.08496,1.21543 -2.1084,1.190625 -3.94394,3.894336 -3.72071,5.531445 -3.72071,12.65039 0,3.398242 0.89297,6.424414 0.89297,3.001367 2.60449,5.308203 3.32383,4.464844 8.11114,4.464844 1.88515,0 3.54707,-0.79375 1.66191,-0.79375 2.72851,-2.182813 1.46348,-1.934765 1.46348,-4.588867 0,-2.455664 -1.33946,-3.54707 -1.31464,-1.091406 -4.36562,-1.091406 v -0.570508 h 15.82539 v 0.570508 q -2.18281,0.198437 -3.07578,0.620117 -0.86816,0.396875 -1.28984,1.389062 -0.47129,1.091407 -0.47129,4.340821 0,0.892968 0.0992,2.778124 l 0.0496,0.570508 q -0.94257,-0.347265 -1.38906,-0.347265 -0.5457,0 -1.38906,0.570507 -2.15801,1.488282 -5.01055,2.282032 -2.85254,0.79375 -6.02754,0.79375 -4.36562,0 -7.88789,-1.488282 -3.49746,-1.488281 -5.68027,-4.266406 -1.63711,-2.083593 -2.53008,-4.960937 -0.86816,-2.877344 -0.86816,-6.101953 0,-4.440039 1.5875,-8.259961 1.5875,-3.819922 4.46484,-6.300391 2.35645,-2.009179 5.43223,-3.100585 3.07578,-1.091407 6.49883,-1.091407 1.98437,0 3.67109,0.372071 1.71152,0.37207 3.62148,1.215429 l 1.46348,0.669727 q 0.5457,0.223242 0.99219,0.223242 0.99219,0 1.51308,-1.612305 z"
        />
      </svg>
    </Link>
  );
};

export default GlobalLogo;